import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './ProfileDropdown.sass'
import { CurrentMemberContext } from 'contexts/CurrentMemberContext'
import Avatar from 'components/Avatar'
import { useOnEscDown } from 'hooks/key-press'
import Link from 'components/Link'
import { isChildrenEmpty } from 'utils/react-util'
import link from 'infra/link'
import { Env } from 'infra/env'
import { getFeatureToggleClient } from 'infra/featureToggles'
import SessionStore from 'iam/SessionStore'
import WorkspaceSignupState from 'iam/views/AuthenticationContainer/WorkspaceSignupContainer/WorkspaceSignupState'
import AdminViewModel from 'admin/AdminViewModel'
import MultiFactorAuthStore from '../../../auth/MultiFactorAuthStore'

interface ProfileDropdownProps {
  visible: boolean
  onRequestClose(): unknown
  sessionStore: SessionStore
  multiFactorAuthStore: MultiFactorAuthStore
  workspaceSignupState: WorkspaceSignupState
  isPayoutsEnabled: boolean
  adminVM: AdminViewModel
  isFirmAdmin: boolean
  isPro: boolean
}

export default observer(function ProfileDropdown({
  onRequestClose,
  visible,
  sessionStore,
  multiFactorAuthStore,
  workspaceSignupState,
  isPayoutsEnabled,
  adminVM,
  isFirmAdmin,
  isPro,
}: ProfileDropdownProps) {
  const currentMember = React.useContext(CurrentMemberContext)
  useOnEscDown(onRequestClose)
  const menuItems = React.useMemo(
    () =>
      currentMember.workspace.config.custom_menu?.items.filter(
        (item) =>
          (item.target === 'All' || item.target === 'Portal') &&
          currentMember.roles.some((r) => r.id === item.audience)
      ) || [],
    [currentMember]
  )

  const toggleClient = getFeatureToggleClient()

  const shouldShowBilling = currentMember.workspace.features.billing.enabled

  const isPlatformAdmin = currentMember.user.platformAdmin

  const showBundleManagerPage = toggleClient.variation(
    'WorkPortal.ShowBundleManagerPage',
    false
  )

  return (
    <div className={cx(styles.root, visible ? styles.visible : styles.hidden)}>
      <div className={styles.overlay} onClick={onRequestClose} />
      <div className={styles.box} onClick={onRequestClose}>
        <Section>
          <div className={styles.profile}>
            <div className={styles.avatarContainer}>
              <Avatar size={32} subject={currentMember} />
            </div>
            <div className={styles.profileContent}>
              <p className={styles.profileTitle}>{currentMember.displayName}</p>
              <p className={styles.profileSubtitle}>{currentMember.email}</p>
            </div>
          </div>
        </Section>
        <Section>
          <SectionTitle>User Account</SectionTitle>
          <SectionItem
            to={link.profileGeneral()}
            data-test="profile-general-page-button"
          >
            Profile
          </SectionItem>
          {shouldShowBilling && (
            <SectionItem
              to={link.profileBilling()}
              data-test="profile-billing-page-button"
            >
              Billing
            </SectionItem>
          )}
          <SectionItem
            to={link.profileNotifications()}
            data-test="profile-notifications-page-button"
          >
            Notifications
          </SectionItem>
          <MultiFactorAuthEnrollmentSectionItem
            multiFactorAuthStore={multiFactorAuthStore}
          />
          {isPro && (
            <SectionItem
              href="https://www.taxfyle.com/pro-support-ticket"
              data-test="profile-pro-support-button"
            >
              Pro support
            </SectionItem>
          )}
          {/* TODO: add back when we have it */}
          {/* {currentMember.workspace.features.billing.couponsReferral.enabled && (
            <SectionItem icon={<Icon name="heart" />}>
              Refer a Friend
            </SectionItem>
          )} */}
        </Section>

        {adminVM.canSeeAdminView && (
          <Section>
            <SectionTitle>Admin</SectionTitle>
            {adminVM.canManageJobScopeChangeRequest && (
              <SectionItem
                to={link.adminChangeRequests()}
                data-test="admin-change-requests-page-button"
              >
                Change requests
              </SectionItem>
            )}
            {adminVM.canSeeStaffingManager && (
              <SectionItem
                to={link.adminStaffingManager()}
                data-test="admin-staffing-manager-button"
              >
                Staffing manager
              </SectionItem>
            )}
            {adminVM.canManageServices && showBundleManagerPage && (
              <SectionItem
                to={link.adminBundleManager()}
                data-test="admin-bundle-manager-button"
              >
                Bundle manager
              </SectionItem>
            )}
            {adminVM.canManageServices && (
              <SectionItem
                to={link.adminServiceManager()}
                data-test="admin-page-button"
              >
                Service manager
              </SectionItem>
            )}
            {isPlatformAdmin && isPayoutsEnabled && (
              <SectionItem
                to={link.adminPayouts()}
                data-test="admin-payouts-button"
              >
                Payouts
              </SectionItem>
            )}
          </Section>
        )}
        <Section>
          {menuItems.map((item, idx) => (
            <SectionItem key={idx} href={item.url}>
              {item.label}
            </SectionItem>
          ))}
        </Section>
        <Section>
          {!isFirmAdmin && (
            <SectionItem
              onClick={() => {
                workspaceSignupState.openWorkspaceSearch()
              }}
            >
              Add New Workspace
            </SectionItem>
          )}
          {currentMember.hasPermission('ADMIN_ACCESS') && (
            <SectionItem onClick={() => gotoHQ(sessionStore)}>
              Go to HQ
            </SectionItem>
          )}
          <SectionItem to={link.logout()}>Log Out</SectionItem>
        </Section>
      </div>
    </div>
  )
})

interface SectionTitleProps {
  children?: React.ReactNode
}

const SectionTitle = ({ children }: SectionTitleProps) => {
  return <p className={styles.sectionTitle}>{children}</p>
}

interface SectionProps {
  children?: React.ReactNode
}

const Section = ({ children }: SectionProps) => {
  if (isChildrenEmpty(children)) {
    return null
  }
  return <div className={styles.section}>{children}</div>
}

interface SectionItemProps {
  children?: React.ReactNode
  icon?: React.ReactNode
  to?: string
  href?: string
  onClick?(): void

  /**
   * Test selector.
   */
  ['data-test']?: string
}

const SectionItem = ({
  children,
  icon,
  href,
  to,
  'data-test': dataTest,
  onClick,
}: SectionItemProps) => {
  return (
    <Link
      to={to}
      href={href}
      className={styles.sectionItem}
      target={href ? '_blank' : undefined}
      onClick={() => onClick && onClick()}
      data-test={dataTest}
    >
      {icon && <div className={styles.sectionItemIcon}>{icon}</div>}
      <div className={styles.sectionItemText}>{children}</div>
    </Link>
  )
}

function gotoHQ(sessionStore: SessionStore) {
  const hqUrl = new URL(Env.HQ_URL)
  hqUrl.searchParams.set('show', 'login')
  if (sessionStore.enterpriseConnection) {
    hqUrl.searchParams.set('connection', sessionStore.enterpriseConnection)
  }
  window.location.assign(hqUrl.toString())
}

const MultiFactorAuthEnrollmentSectionItem = observer(
  function MultiFactorAuthEnrollmentSectionItem({
    multiFactorAuthStore,
  }: {
    multiFactorAuthStore: MultiFactorAuthStore
  }) {
    if (
      !getFeatureToggleClient().variation(
        'Portals.MultiFactorAuthentication',
        false
      )
    ) {
      return null
    }

    const click = async () => {
      const url = await multiFactorAuthStore.getEnrollmentUrl()
      window.open(url)
    }

    // If the user is already enrolled in MFA, then don't show the option
    // to enroll again.
    // There is a caveat with this approach: we won't observe that the user
    // has enrolled in MFA until the next time they log in.
    if (multiFactorAuthStore.isUserEnrolled) {
      return null
    }

    return (
      <SectionItem onClick={click}>
        Enroll in multi-factor authentication
      </SectionItem>
    )
  }
)
